"use client";
import { useValuationFormReady } from "./use-valuation-form-ready";
import type { ApiOutput } from "@repo/api/trpc/router";
import { useEffect, useRef, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import dynamic from "next/dynamic";
import { trackValuation } from "./valuation-analytics";
import {
  captureBrokerLeadAttributionFromUrl,
  getBrokerLeadAttributionForSubmit,
} from "@/lib/broker-lead-attribution";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api-client";
import { ValuationPublicDialog } from "./valuation-public-dialog";
import { PortalBrand } from "@/components/flow/portal-brand";
import { neutralBrokerLeadLanding } from "@repo/utils/lib/broker-lead-landing-content";

const ValuationQuestionnaire = dynamic(() =>
  import("./valuation-questionnaire").then(
    (module) => module.ValuationQuestionnaire,
  ),
);
export type ValuationFlowProps = {
  skipWelcome?: boolean;
  campaignToken?: string;
  formToken?: string;
  brokerHandle?: string;
  campaignPresentation?: ApiOutput["listings"]["valuation"]["getLeadSession"]["campaignPresentation"];
  industry?: { id: number; name: string };
  region?: string;
};
export function ValuationFlow(props: ValuationFlowProps) {
  const isPublic =
    !props.campaignToken && !props.brokerHandle && !props.formToken;
  useValuationFormReady(!isPublic);
  const params = useSearchParams();
  const pathname = usePathname();
  const [sessionId, setSessionId] = useState(params.get("vs"));
  const [open, setOpen] = useState(Boolean(params.get("vs")));
  const [error, setError] = useState<string | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const start = api.listings.valuation.startLeadSession.useMutation();
  const session = api.listings.valuation.getLeadSession.useQuery(
    { sessionId: sessionId ?? "" },
    { enabled: Boolean(sessionId), retry: false, refetchOnWindowFocus: false },
  );
  async function begin() {
    setError(null);
    if (sessionId && session.data) {
      await session.refetch();
      setOpen(true);
      return;
    }
    if (isPublic) setOpen(true);
    try {
      captureBrokerLeadAttributionFromUrl();
      const tracking = getBrokerLeadAttributionForSubmit();
      const result = await start.mutateAsync({
        tracking,
        campaignToken: props.campaignToken,
        formToken: props.formToken,
        brokerHandle: props.brokerHandle || props.formToken,
        campaignId: params.get("utm_campaign") ?? undefined,
        sourceContext: {
          utmSource: params.get("utm_source") ?? undefined,
          utmMedium: params.get("utm_medium") ?? undefined,
          utmCampaign: params.get("utm_campaign") ?? undefined,
          utmContent: params.get("utm_content") ?? undefined,
        },
        creativeId: params.get("utm_content") ?? undefined,
        landingPath: pathname,
        channel: params.get("utm_source") === "meta" ? "meta" : "direct",
      });
      trackValuation({
        stage: "started",
        sessionId: result.sessionId,
        landingPath: pathname,
        entryKind: props.campaignToken
          ? "CAMPAIGN"
          : props.brokerHandle || props.formToken
            ? "BROKER"
            : "PUBLIC",
      });
      setSessionId(result.sessionId);
      const url = new URL(window.location.href);
      url.searchParams.set("vs", result.sessionId);
      // Let Next synchronize its URL state so a later navigation retains the session.
      window.history.replaceState({}, "", url);
      if (!isPublic) setOpen(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Please try again.");
    }
  }
  const beginRef = useRef(begin);
  beginRef.current = begin;
  const autoStarted = useRef(false);
  useEffect(() => {
    if (!props.skipWelcome || sessionId || autoStarted.current) return;
    autoStarted.current = true;
    void beginRef.current();
  }, [props.skipWelcome, sessionId]);
  const loadError = error ?? session.error?.message ?? null;
  const presentation =
    session.data?.campaignPresentation ?? props.campaignPresentation;
  const intro =
    presentation?.landingIntro ??
    neutralBrokerLeadLanding("en", "FREE_VALUATION");
  const questions =
    open && session.data ? (
      <ValuationQuestionnaire
        key={session.data.sessionId}
        data={session.data}
        defaults={props}
        onClose={() => setOpen(false)}
      />
    ) : null;
  if (isPublic)
    return (
      <>
        <Button
          ref={triggerRef}
          className="min-h-11"
          onClick={() => void begin()}
          disabled={start.isPending || session.isFetching}
          aria-haspopup="dialog"
          aria-expanded={open}
        >
          {start.isPending || session.isFetching
            ? "Getting ready…"
            : session.data?.complete
              ? "View my estimate"
              : session.data
                ? "Continue my estimate"
                : "Start free valuation"}
        </Button>
        <ValuationPublicDialog
          open={open}
          onOpenChange={setOpen}
          triggerRef={triggerRef}
        >
          {questions}
          {!session.data && (
            <div className="p-6 pt-16">
              {error || session.error ? (
                <div className="space-y-4">
                  <p role="alert">{loadError}</p>
                  <Button
                    onClick={() => void begin()}
                    disabled={start.isPending}
                  >
                    Try again
                  </Button>
                </div>
              ) : (
                <output>Opening your estimate…</output>
              )}
            </div>
          )}
        </ValuationPublicDialog>
      </>
    );
  return (
    <section
      id="valuation-tool-form"
      data-valuation-flow
      aria-label="Free business valuation"
      className={
        props.campaignToken || props.brokerHandle || props.formToken
          ? "bg-background"
          : "mx-auto w-full max-w-4xl scroll-mt-24 overflow-hidden rounded-2xl border bg-background shadow-sm"
      }
    >
      {props.skipWelcome && !session.data && !loadError && (
        <output className="p-8 text-center text-muted-foreground">
          Getting your questions ready…
        </output>
      )}
      {!(open && session.data) && (!props.skipWelcome || loadError) && (
        <div className="mx-auto max-w-2xl space-y-6 px-6 py-10 sm:px-8 sm:py-14">
          {presentation && (
            <PortalBrand
              name={presentation.name}
              avatarUrl={presentation.avatarUrl}
              subtitle="Confidential"
            />
          )}
          <p className="text-sm font-medium text-muted-foreground">
            Free Business Valuation
          </p>
          <h2 className="text-balance text-3xl font-semibold tracking-tight sm:text-4xl">
            {presentation
              ? intro.headline
              : "Find out what your business could be worth"}
          </h2>
          <p className="my-4 text-muted-foreground">
            {presentation
              ? intro.description
              : "A few simple questions. A clear starting range, with the reasons behind it."}
          </p>
          <Button
            data-form-ready-control
            className="min-h-11"
            onClick={() => void begin()}
            disabled={start.isPending}
          >
            {start.isPending
              ? "Getting ready…"
              : session.data
                ? "Continue my estimate"
                : "Get my free estimate"}
          </Button>
          {loadError && (
            <p role="alert" className="mt-4">
              {loadError}
            </p>
          )}
          <p className="text-sm text-muted-foreground">
            No documents to find. Rough numbers are fine.
          </p>
        </div>
      )}
      {questions}
      {open && session.isLoading && (
        <p aria-live="polite">Opening your saved estimate…</p>
      )}
    </section>
  );
}
